import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../components/recharts/components";
import {
  getPowerExchangeCollectiveData,
  getPowerExchangeDemandGeneration,
} from "../../../../../../Rest_api/RestApi";
import loader from "../../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../../extensions/date";

// Metric filter options with colors matching the chart image
const MetricOptions = [
  { id: "mcp", label: "MCP", color: "#7838F9" },
  { id: "purchase_bid", label: "Purchase Bid", color: "#ff9f43" },
  { id: "mcv", label: "MCV", color: "#9C3C1F" },
  { id: "sell_bid", label: "Sell Bid", color: "#5BE854" },
];

interface CollectiveDataItem {
  date?: string;
  time_slot: number;
  purchase_bid_mw: number;
  sell_bid_mw: number;
  mcv_mw: number;
  weighted_avg_price: number;
}

interface ApiResponse {
  results: CollectiveDataItem[];
}

interface DemandGenerationResponse {
  peak_demand: number | null;
  results: DemandGenerationItem[];
}

interface ProcessedData {
  date: string;
  time_slot: number;
  month: string;
  weighted_avg_price: number;
  purchase_bid_mw: number;
  mcv_mw: number;
  sell_bid_mw: number;
  demand_met_mw?: number;
  total_generation_mw?: number;
  [key: string]: any;
}

interface DemandGenerationItem {
  date: string;
  time_slot: number;
  demand_met_mw: number;
  total_generation_mw: number;
}

const Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["time_slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

function MetricSelector({
  selected,
  onSelectionChange,
}: {
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
}) {
  const toggleSelection = (id: string) => {
    if (selected.includes(id)) {
      // Don't allow deselecting if it's the last one
      if (selected.length > 1) {
        onSelectionChange(selected.filter((s) => s !== id));
      }
    } else {
      onSelectionChange([...selected, id]);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {MetricOptions.map((option) => {
        const isSelected = selected.includes(option.id);
        return (
          <button
            key={option.id}
            onClick={() => toggleSelection(option.id)}
            className={`flex items-center gap-2 px-2 py-1 rounded-full transition-all duration-200 ${
              isSelected
                ? "bg-white shadow-sm border border-gray-200"
                : "bg-transparent hover:bg-gray-100"
            }`}
          >
            <div
              className={`w-3 h-3 rounded transition-all duration-200 ${
                isSelected ? "opacity-100" : "opacity-40"
              }`}
              style={{ backgroundColor: option.color }}
            />
            <span
              className={`font-medium text-xs transition-all duration-200 ${
                isSelected ? "text-gray-700" : "text-gray-500"
              }`}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function PowerInjectionAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedExchanges,
  selectedMarkets,
  setSelectedBlock,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedExchanges?: string[];
  selectedMarkets?: string[];
  setSelectedBlock?: React.Dispatch<React.SetStateAction<any[]>>;
}) {
  // Read dates directly from Redux - Use "Power Market" key consistently
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [data, setData] = useState<ProcessedData[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [maxDemandMet, setMaxDemandMet] = useState<number | null>(null);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(["mcp"]);

  useEffect(() => {
    // Use "Power Market" as the key - must match PowerAnalyticsDefaultDates
    const dateKey = "Power Market";

    if (!DefaultDates?.[dateKey]?.defaultTrendDates) {
      console.log("No data available for Power Market");
      return;
    }

    const trendDates = DefaultDates[dateKey].defaultTrendDates;

    const fetchData = async () => {
      try {
        setLoading(true);
        const dataMapByTime = new Map<number, any>();
        const peakDemandsByDate = new Map<string, number>();

        // Initialize map with all 96 time slots to ensure continuity
        for (let i = 1; i <= 96; i++) {
          dataMapByTime.set(i, { time_slot: i });
        }

        const promises = trendDates.map(async (dateObj: any) => {
          const { value, label } = dateObj;
          const [startDate, endDate] = value.split(",");

          // Sanitize label for key usage (remove spaces, special chars)
          const safeLabel = label.replace(/[^a-zA-Z0-9]/g, "");

          console.log("Fetching for range:", {
            startDate,
            endDate,
            label: safeLabel,
          });

          const [collectiveResponse, demandGenResponse] = await Promise.all([
            getPowerExchangeCollectiveData(
              startDate,
              endDate,
              selectedExchanges,
              selectedMarkets,
            ),
            getPowerExchangeDemandGeneration(startDate, endDate),
          ]);

          const response: any = collectiveResponse;
          // Handle both direct array response and results wrapper
          const results = Array.isArray(response)
            ? response
            : response?.results || [];
          const demandGenApiResponse: DemandGenerationResponse =
            demandGenResponse;
          const demandGenResults: DemandGenerationItem[] =
            demandGenApiResponse?.results || [];

          // Capture peak demand for this specific date range
          if (demandGenApiResponse?.peak_demand != null) {
            console.log(
              `Peak Demand for ${safeLabel}:`,
              demandGenApiResponse.peak_demand,
            );
            peakDemandsByDate.set(safeLabel, demandGenApiResponse.peak_demand);
          } else {
            console.log(
              `No Peak Demand found for ${safeLabel}`,
              demandGenApiResponse,
            );
          }

          // Create a map for demand-generation data for quick lookup using time_slot
          const demandGenMap = new Map<number, DemandGenerationItem>();
          demandGenResults.forEach((item: DemandGenerationItem) => {
            demandGenMap.set(item.time_slot, item);
          });

          const keyPrefix = `${safeLabel}`; // Suffix for metrics

          // Fill Demand/Generation for all slots first
          for (let i = 1; i <= 96; i++) {
            const slotData = dataMapByTime.get(i);
            if (!slotData) continue;

            // Initialize metrics to 0
            if (slotData[`purchase_bid_mw_${keyPrefix}`] === undefined) {
              slotData[`purchase_bid_mw_${keyPrefix}`] = 0;
              slotData[`sell_bid_mw_${keyPrefix}`] = 0;
              slotData[`mcv_mw_${keyPrefix}`] = 0;
              slotData[`weighted_avg_price_${keyPrefix}`] = 0;
              slotData[`demand_met_${keyPrefix}`] = 0;
              slotData[`total_generation_${keyPrefix}`] = 0;
            }

            // Set Demand/Generation from map
            const demandItem = demandGenMap.get(i);
            if (demandItem) {
              slotData[`demand_met_${keyPrefix}`] = demandItem.demand_met_mw;
              slotData[`total_generation_${keyPrefix}`] =
                demandItem.total_generation_mw;
            }
          }

          // Process and aggregate data for this date range
          results.forEach((item: CollectiveDataItem) => {
            const timeSlot = item.time_slot;
            const slotData = dataMapByTime.get(timeSlot);

            if (slotData) {
              slotData[`purchase_bid_mw_${keyPrefix}`] +=
                item.purchase_bid_mw || 0;
              slotData[`sell_bid_mw_${keyPrefix}`] += item.sell_bid_mw || 0;
              slotData[`mcv_mw_${keyPrefix}`] += item.mcv_mw || 0;
              slotData[`weighted_avg_price_${keyPrefix}`] +=
                item.weighted_avg_price || 0;
            }
          });
        });

        await Promise.all(promises);

        // Convert map to array and sort by time_slot
        const pivotedData = Array.from(dataMapByTime.values()).sort((a, b) => {
          return a.time_slot - b.time_slot;
        });

        // Add peak demand to the data (as constant fields per date)
        pivotedData.forEach((row) => {
          peakDemandsByDate.forEach((val, label) => {
            row[`peak_demand_${label}`] = val;
          });
        });

        console.log("Pivoted data size:", pivotedData.length);
        if (pivotedData.length > 0)
          console.log("Sample pivot:", pivotedData[0]);

        setData(pivotedData); // This is now 'chartData' source
        setChartData(pivotedData);

        // Also update chartKeys or similar used for legends?
        // We need to store variable keys
        const dynamicKeys: any[] = [];
        trendDates.forEach((dateObj: any) => {
          const safeLabel = dateObj.label.replace(/[^a-zA-Z0-9]/g, "");
          // Add keys for this date
          dynamicKeys.push({ label: dateObj.label, keySuffix: safeLabel });
        });
        setChartKeys(dynamicKeys); // Reuse this state to store date keys
      } catch (error) {
        console.error("Error fetching data", error);
        setError?.("An error occurred while fetching data.");
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [DefaultDates, refreshKey, setError, selectedExchanges, selectedMarkets]);

  const handlexDataKey = useCallback(() => {
    return "time_slot";
  }, [selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    // Data is already filtered by date range from API call
    // No need to filter again here
    let filteredData = [...data];

    // Add Peak Demand as a constant value on each data point (for horizontal line)
    // Use 0 if null so the line always shows
    const dataWithPeakDemand = filteredData.map((item) => ({
      ...item,
      peak_demand_mw: maxDemandMet ?? 0,
    }));

    console.log("Chart data prepared:", dataWithPeakDemand.length, "items");

    setChartData(dataWithPeakDemand);
    // Do NOT clear chartKeys here as they are needed for dynamic legends
    // setChartKeys([]);
  }, [data, selectedInterval, selectedMetrics, maxDemandMet]);

  const chartSuffixes = useMemo(() => {
    const metrics = [];

    // Peak Demand - Always visible (permanent red line) - show even when null (as 0)
    metrics.push({
      suffix: "peak_demand_mw",
      displayName: "Peak Demand",
      stackSuffix: "",
      stroke: "#e74c3c", // Fixed Red color for Peak Demand
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      isDash: true,
    });

    // MCP - Purple line
    if (selectedMetrics.includes("mcp")) {
      metrics.push({
        suffix: "weighted_avg_price",
        displayName: "MCP",
        stackSuffix: "",
        chartType: ChartType.Line,
        unit: "Rs/MWh",
        yAxisId: "right",
      });
    }

    // Purchase Bid - Orange line
    if (selectedMetrics.includes("purchase_bid")) {
      metrics.push({
        suffix: "purchase_bid_mw",
        displayName: "Purchase Bid",
        stackSuffix: "",
        chartType: ChartType.Line,
        unit: "MW",
        yAxisId: "left",
      });
    }

    // MCV - Blue line
    if (selectedMetrics.includes("mcv")) {
      metrics.push({
        suffix: "mcv_mw",
        displayName: "MCV",
        stackSuffix: "",
        chartType: ChartType.Line,
        unit: "MW",
        yAxisId: "left",
      });
    }

    // Sell Bid - Green line
    if (selectedMetrics.includes("sell_bid")) {
      metrics.push({
        suffix: "sell_bid_mw",
        displayName: "Sell Bid",
        stackSuffix: "",
        chartType: ChartType.Line,
        unit: "MW",
        yAxisId: "left",
      });
    }

    // All India Demand - Always visible (green line)
    metrics.push({
      suffix: "demand_met_mw",
      displayName: "All India Demand",
      stackSuffix: "",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    });

    // All India Generation - Always visible (teal line)
    metrics.push({
      suffix: "total_generation_mw",
      displayName: "All India Generation",
      stackSuffix: "",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    });

    return metrics;
  }, [selectedMetrics, maxDemandMet]);

  // Legend generation logic
  const legends = useMemo(() => {
    const result: any[] = [];
    if (!chartKeys || chartKeys.length === 0) return [];

    chartSuffixes.forEach(
      ({ suffix, displayName, chartType, unit, yAxisId, isDash, stroke }) => {
        chartKeys.forEach((dateKeyObj: any) => {
          const { label, keySuffix } = dateKeyObj;

          // Logic to match suffix with pivoted data keys from fetchData
          // Default: suffix_keySuffix
          let dataKey = `${suffix}_${keySuffix}`;

          // Special handling for mismatches between suffix in chartSuffixes and keys used in fetchData pivot
          if (suffix === "peak_demand_mw") {
            dataKey = `peak_demand_${keySuffix}`;
          } else if (suffix === "demand_met_mw") {
            dataKey = `demand_met_${keySuffix}`;
          } else if (suffix === "total_generation_mw") {
            dataKey = `total_generation_${keySuffix}`;
          }

          result.push({
            dataKey: dataKey,
            name: `${displayName} (${label})`,
            type: chartType,
            unit: unit,
            yAxisId: yAxisId,
            stroke: stroke, // Use fixed color if defined (e.g. Peak Demand), else undefined (random)
            isDash: isDash,
          });
        });
      },
    );

    return result;
  }, [chartSuffixes, chartKeys]);

  function secondDataKey() {
    if (selectedInterval.isTime && selectedBlock.length > 0) {
      return "date";
    }
    if (selectedInterval.isTime) {
      return "date";
    }
    return undefined;
  }

  // Handle block click for selection - update parent state
  const handleBlockClick = (params: any) => {
    if (!setSelectedBlock) return;

    const clickedValue = params.name;
    if (!clickedValue) return;

    setSelectedBlock((prev) => {
      if (prev.includes(clickedValue)) {
        return prev.filter((v) => v !== clickedValue);
      } else {
        const newBlocks = [...prev, clickedValue];
        if (selectedInterval.isTime) {
          return newBlocks.sort((a, b) => Number(a) - Number(b));
        }
        return newBlocks;
      }
    });
  };

  return (
    <div className="dash-card !border !border-[#DFEAF2] bg-white w-full !max-h-[300px] rounded-2xl p-2">
      <div className="flex justify-between items-center mb-2 border-b border-[#DFEAF2] bg-[#FFF] pb-1 flex-shrink-0 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="chartHeading !text-[#34656D] !text-sm">
            Day-wise Injection
          </h2>
        </div>
        <div className="flex gap-x-3 justify-end flex-shrink-0">
          <MetricSelector
            selected={selectedMetrics}
            onSelectionChange={setSelectedMetrics}
          />
        </div>
      </div>

      {loading ? (
        <div className="w-full bg-white flex items-center justify-center h-full">
          <img src={loader} width={200} alt="loader" className="block" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center h-full">
          <p className="text-gray-500">
            No data available for the selected date range
          </p>
        </div>
      ) : (
        <>
          <ReMixChart
            chartHeight="calc(100% - 50px)"
            data={chartData}
            showBrush={chartData.length > 96 ? true : undefined}
            brushStart={BrushStart.Start}
            isTimeSlot={selectedInterval.isTime}
            onlyTitle={true}
            legends={legends}
            yAxisLabel="MW"
            nameGap={40}
            secondYAxisLabel={"Rs/MWh"}
            xDataKey={xDataKey}
            top={10}
            bottom={30}
            xTick={
              selectedInterval.isTime && selectedBlock.length === 0
                ? (value: any, index: any) => renderHourTick(value, 10)
                : undefined
            }
            onBarClick={handleBlockClick}
            highlightedXValues={selectedBlock}
            enableClickSelection={true}
            // secondXDataKey={secondDataKey()} // Removed to prevent "undefined" label
            showToolbox={false}
            fontSize={10}
            isSmallLegend={true}
            xaxisHeight={50}
          />
        </>
      )}
    </div>
  );
}
